import {BitmapText, FederatedPointerEvent, Graphics, IBitmapTextStyle, Matrix, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {MdDoorLite, MdDoorLiteField} from "../../model/door/MdDoorLite";
import {inches, lineBlueHeavy, lineBlueLight, lineRed} from "../../const";
import {getGlassTexture} from "../frameGrid/item/LiteFace";
import {SNAP_PX, SnapGuide, snapLiteMove, snapLiteResize, SnapStiles} from "./liteSnap";

// a selected lite gets a distinct bright-blue outline + a stronger wash so it
// clearly reads as selected against the unselected lites.
const SELECTED_COLOR = lineBlueLight;
const SELECTED_ALPHA = 0.55;
const SELECTED_STROKE = 4;
const UNSELECTED_STROKE = 2;

// the wash over an unselected lite that crosses a stile line.
const WARNING_ALPHA = 0.3;

// how far the tappable value sits off its dimension line.
const LABEL_OFFSET = 1.5 * inches;

// half-length of the perpendicular end ticks on a dimension line.
const TICK = 1 * inches;

// inset of the inner width/height dimension lines from the lite's top/left edge.
const INNER_INSET = 3 * inches;

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

// smallest a lite can be resized to, per axis.
const MIN_SIZE = 2 * inches;

// how far a snap guide runs past the leaf edge, so the line is visible beyond the lite.
const GUIDE_OVERRUN = 2 * inches;

// half-size of the square resize handle drawn at each edge midpoint + corner.
const HANDLE_HALF = 0.3 * inches;
// hit padding beyond the drawn square so handles stay easy to grab despite the
// small visual size.
const HANDLE_HIT_PAD = 0.9 * inches;

// which edges a resize handle moves. `h`/`v` name the horizontal/vertical edge
// it drives (null = that axis is unchanged, i.e. an edge handle rather than a
// corner). `cursor` is the matching resize cursor.
type HSide = "left" | "right" | null;
type VSide = "top" | "bottom" | null;
type HandleDesc = {h: HSide; v: VSide; cursor: string};

const HANDLES: HandleDesc[] = [
    {h: "left", v: "top", cursor: "nwse-resize"},
    {h: null, v: "top", cursor: "ns-resize"},
    {h: "right", v: "top", cursor: "nesw-resize"},
    {h: "right", v: null, cursor: "ew-resize"},
    {h: "right", v: "bottom", cursor: "nwse-resize"},
    {h: null, v: "bottom", cursor: "ns-resize"},
    {h: "left", v: "bottom", cursor: "nesw-resize"},
    {h: "left", v: null, cursor: "ew-resize"},
];

type DimSpec = {
    field: MdDoorLiteField;
    vertical: boolean;
    // the dimension line's endpoints (leaf-local scene coords).
    x1: number; y1: number; x2: number; y2: number;
};

// Renders one glass lite as a rectangle within the door leaf, plus its six
// dimensions: the four edge offsets (top/left/right/bottom, drawn in the gap
// between the lite and the leaf edge) and the lite's own width/height (drawn
// inside it). Each value is tappable to edit; editing flexes the other
// dimensions on that axis via MdDoorLite. The rectangle itself is clickable to
// select the lite (for removal from the Selected tab).
export class DoorLiteThing extends DrawFrmThing {
    private elev: ElevationApp;
    private lite: MdDoorLite;
    private leafSize: () => {width: number; height: number};
    private onEdit: () => void;
    private onSelect: (additive: boolean) => void;

    // whether this lite is the current canvas selection. Tracked from the
    // selection emitter's argument passed to the subscriber, which is the
    // clearest source of the newly-selected value during an emit.
    private selectedFlag = false;

    private glass: Graphics;
    private dims: Graphics;
    // snap guides, drawn only while a move gesture is locked onto something.
    private guides: Graphics;
    private labels = new Map<MdDoorLiteField, BitmapText>();
    // the lite's reference mark ("G1") in the middle of the lite. Always drawn — the
    // submittal cites a lite by its mark, so it must be on the exported drawing whether
    // or not the dimensions are.
    private markLabel: BitmapText;

    // one interactive square per HANDLES entry, shown only while selected. Kept
    // alongside its descriptor so a pointerdown knows which edges to move.
    private handles: {g: Graphics; desc: HandleDesc}[] = [];

    // the active gesture, or null. "move" drags the whole lite; "resize" drags
    // one/two edges. Both record the leaf-local pointer position at the start plus
    // the geometry to offset from. Bound handlers are stored so they can be
    // detached on pointer-up / destroy.
    private gesture:
        | {kind: "move"; px: number; py: number; left: number; top: number}
        | {kind: "resize"; px: number; py: number; desc: HandleDesc; left: number; top: number; right: number; bottom: number}
        | null = null;
    private onGestureMove = (e: FederatedPointerEvent) => this.gestureMove(e);
    private onGestureEnd = () => this.endGesture(true);

    constructor(props: {
        elev: ElevationApp;
        lite: MdDoorLite;
        leafSize: () => {width: number; height: number};
        onEdit: () => void;
        onSelect: (additive: boolean) => void;
    }) {
        super();
        this.elev = props.elev;
        this.lite = props.lite;
        this.leafSize = props.leafSize;
        this.onEdit = props.onEdit;
        this.onSelect = props.onSelect;

        this.glass = new Graphics();
        this.dims = new Graphics();
        this.guides = new Graphics();
        // inert: a guide is feedback, never a hit target.
        this.guides.eventMode = "none";
        this.lines.addChild(this.glass);
        this.lines.addChild(this.dims);
        this.lines.addChild(this.guides);

        this.markLabel = new BitmapText("", TEXT_STYLE);
        this.markLabel.anchor.set(0.5, 0.5);
        this.markLabel.tint = lineBlueHeavy;
        // inert: taps fall through to the glass rect so it still selects/drags.
        this.markLabel.eventMode = "none";
        this.lines.addChild(this.markLabel);

        // the filled rect is auto hit-tested, so a tap anywhere on the glass
        // selects the lite. Dimension labels stopPropagation so editing a value
        // doesn't also select.
        this.makeInteractive({
            target: this.glass,
            onClick: (e) => this.onSelect(e.ctrlKey || e.metaKey),
        });

        // press-and-drag the glass to reposition the lite within the leaf. A plain
        // tap (no drag) still selects via makeInteractive's pointertap above.
        if (this.elev.readOnly) {
            this.glass.cursor = "pointer";
        } else {
            this.glass.cursor = "move";
            this.glass.on("pointerdown", (e: FederatedPointerEvent) => this.beginMove(e));
        }

        // edge/corner resize handles, drawn on top of the glass + dims. Positioned
        // and shown per-draw (only while selected). None when view only.
        for (const desc of this.elev.readOnly ? [] : HANDLES) {
            const g = new Graphics();
            g.eventMode = "static";
            g.cursor = desc.cursor;
            g.visible = false;
            g.on("pointerdown", (e: FederatedPointerEvent) => this.beginResize(desc, e));
            this.lines.addChild(g);
            this.handles.push({g, desc});
        }

        // keep the highlight in sync with the canvas selection, and repaint in
        // place (no full relayout) when it changes.
        this.selectedFlag = this.elev.viewSettings.isSelected(this.lite.id);
        this.trackSubscription(this.elev.viewSettings.selection.subscribe(() => {
            const nextSelected = this.elev.viewSettings.isSelected(this.lite.id);
            if (nextSelected === this.selectedFlag) return;
            this.selectedFlag = nextSelected;
            if (!this.destroyed) this.draw();
        }));

        this.draw();
    }

    // ---- drag to move / resize ----------------------------------------------

    private get stage() {
        return this.elev.draw.pixi.stage;
    }

    // convert a global (screen) pointer position into leaf-local scene coords.
    // this.lines sits at the leaf's origin, so its local space is leaf-local, and
    // toLocal accounts for the current pan/zoom + layout scale.
    private toLeafLocal(global: {x: number; y: number}) {
        return this.lines.toLocal(global as any);
    }

    // press-and-drag the glass body: reposition the whole lite.
    private beginMove(e: FederatedPointerEvent) {
        // ctrl/meta-click is a (multi-)select, not a drag: bail before starting the
        // gesture so the pointertap handler's additive select can extend the
        // selection instead of the gesture's non-additive select replacing it.
        if(e.ctrlKey || e.metaKey) { e.stopPropagation(); return; }

        const local = this.startGesture(e);
        this.gesture = {
            kind: "move",
            px: local.x,
            py: local.y,
            left: this.lite.offsetLeft.get(),
            top: this.lite.offsetTop.get(),
        };
    }

    // press-and-drag an edge/corner handle: resize by moving that edge (or two,
    // at a corner), holding the opposite edges.
    private beginResize(desc: HandleDesc, e: FederatedPointerEvent) {
        // ctrl/meta-click never drags (see beginMove); leave the selection to the tap.
        if(e.ctrlKey || e.metaKey) { e.stopPropagation(); return; }

        const local = this.startGesture(e);
        const lx = this.lite.offsetLeft.get();
        const ty = this.lite.offsetTop.get();
        this.gesture = {
            kind: "resize",
            px: local.x,
            py: local.y,
            desc,
            left: lx,
            top: ty,
            right: lx + this.lite.width.get(),
            bottom: ty + this.lite.height.get(),
        };
    }

    // shared gesture setup: stop the pan / leaf-deselect, select the lite, and
    // attach the global move + end listeners. Returns the starting leaf-local pos.
    private startGesture(e: FederatedPointerEvent) {
        e.stopPropagation();
        // a drag selects just this lite (no additive modifier on a gesture start).
        this.onSelect(false);

        this.elev.draw.setPanSuspended(true);
        this.stage.eventMode = "static";
        this.stage.on("globalpointermove", this.onGestureMove);
        window.addEventListener("pointerup", this.onGestureEnd);
        window.addEventListener("pointercancel", this.onGestureEnd);

        return this.toLeafLocal(e.global);
    }

    private gestureMove(e: FederatedPointerEvent) {
        const g = this.gesture;
        if (!g) return;

        const {width: LW, height: LH} = this.leafSize();
        const local = this.toLeafLocal(e.global);
        const dx = local.x - g.px;
        const dy = local.y - g.py;

        const others = this.elev.doorSpec.litesFor(this.lite.leaf).filter(l => l.id !== this.lite.id);

        if (g.kind === "move") {
            // hold alt to place freely. Otherwise the lite locks onto the leaf's centrelines,
            // the stile lines and the other lites' edges + centres, per axis (see snapLiteMove).
            const snapped = e.altKey ? null : snapLiteMove({
                left: g.left + dx,
                top: g.top + dy,
                width: this.lite.width.get(),
                height: this.lite.height.get(),
                leafWidth: LW,
                leafHeight: LH,
                others,
                stiles: this.stiles(),
                tolerance: this.snapTolerance(),
            });

            // moveTo clamps the top-left so the rectangle stays inside the leaf. A snap that
            // would have pushed the lite out (e.g. its right edge onto a line closer to the
            // leaf edge than the lite is wide) is clamped away, so only the guides whose snap
            // actually held are drawn.
            const wantLeft = snapped?.left ?? g.left + dx;
            const wantTop = snapped?.top ?? g.top + dy;
            this.lite.moveTo(wantLeft, wantTop, LW, LH);

            const heldX = this.lite.offsetLeft.get() === wantLeft;
            const heldY = this.lite.offsetTop.get() === wantTop;
            this.drawGuides((snapped?.guides ?? []).filter(gu => gu.axis === "x" ? heldX : heldY));
        } else {
            // move only the dragged edges; hold the opposite ones. A dragged edge locks
            // onto a stile line, the leaf centre or another lite's line unless alt is held
            // (see snapLiteResize). Each edge is then clamped to the leaf and kept at least
            // MIN_SIZE from its opposite.
            let {left, top, right, bottom} = g;
            if (g.desc.h === "left") left = g.left + dx;
            else if (g.desc.h === "right") right = g.right + dx;
            if (g.desc.v === "top") top = g.top + dy;
            else if (g.desc.v === "bottom") bottom = g.bottom + dy;

            const snapped = e.altKey ? null : snapLiteResize({
                left, top, right, bottom,
                dragH: g.desc.h,
                dragV: g.desc.v,
                leafWidth: LW,
                leafHeight: LH,
                others,
                stiles: this.stiles(),
                tolerance: this.snapTolerance(),
            });
            if (snapped) ({left, top, right, bottom} = snapped);

            if (g.desc.h === "left") left = clamp(left, 0, right - MIN_SIZE);
            else if (g.desc.h === "right") right = clamp(right, left + MIN_SIZE, LW);
            if (g.desc.v === "top") top = clamp(top, 0, bottom - MIN_SIZE);
            else if (g.desc.v === "bottom") bottom = clamp(bottom, top + MIN_SIZE, LH);

            this.lite.setRect(left, top, right, bottom, LW, LH);

            // only the guides whose snap survived the clamp are drawn.
            const held = (gu: SnapGuide) => gu.axis === "x"
                ? (g.desc.h === "left" ? left : right) === gu.at
                : (g.desc.v === "top" ? top : bottom) === gu.at;
            this.drawGuides((snapped?.guides ?? []).filter(held));
        }

        this.draw();
    }

    // report is false on destroy, where the gesture is abandoned rather than finished
    private endGesture(report: boolean) {
        const g = this.gesture;
        if (!g) return;
        this.gesture = null;
        this.drawGuides([]);

        this.stage.off("globalpointermove", this.onGestureMove);
        window.removeEventListener("pointerup", this.onGestureEnd);
        window.removeEventListener("pointercancel", this.onGestureEnd);
        this.elev.draw.setPanSuspended(false);

        // report a finished drag/resize as a lite layout edit, like align/distribute
        if (report && this.gestureChanged(g)) {
            this.elev.doorSpec.litesChanged.emit("layout");
        }
    }

    private gestureChanged(g: NonNullable<DoorLiteThing["gesture"]>): boolean {
        const left = this.lite.offsetLeft.get();
        const top = this.lite.offsetTop.get();
        if (left !== g.left || top !== g.top) return true;
        if (g.kind === "move") return false;

        return left + this.lite.width.get() !== g.right || top + this.lite.height.get() !== g.bottom;
    }

    getLite(): MdDoorLite {
        return this.lite;
    }

    destroy() {
        this.endGesture(false);
        super.destroy();
    }

    private get app() {
        return this.elev.draw;
    }

    // SNAP_PX in leaf-local units at the current zoom, so the snap feels the same however
    // far in or out the drawing is. worldTransform.a is the leaf's on-screen scale; a
    // degenerate scale falls back to a fixed distance rather than snapping everything.
    private snapTolerance(): number {
        const scale = Math.abs(this.lines.worldTransform.a);
        if(!isFinite(scale) || scale <= 0) return 0.25 * inches;
        return SNAP_PX / scale;
    }

    // the leaf's stiles as snap lines.
    private stiles(): SnapStiles {
        const door = this.elev.doorSpec;
        return {side: door.stileSide.get(), top: door.stileTop.get(), bottom: door.stileBottom.get()};
    }

    // draw the lines the lite is currently locked onto, running the full leaf plus a little
    // overrun. A leaf centre line is red, a stile line the heavy blue, a sibling lite's line
    // the selection blue. Called with an empty list to clear them.
    private drawGuides(list: SnapGuide[]) {
        const g = this.guides;
        g.clear();
        if(list.length === 0) return;

        const {width: LW, height: LH} = this.leafSize();

        // this.lines sits at the leaf's origin (the glass rect is drawn at the lite's leaf-local
        // offsets), so a guide's coordinate needs no adjusting.
        for(const guide of list) {
            g.lineStyle(1, guide.kind === "centre" ? lineRed : guide.kind === "stile" ? lineBlueHeavy : SELECTED_COLOR, 0.9);

            if(guide.axis === "x") {
                g.moveTo(guide.at, -GUIDE_OVERRUN);
                g.lineTo(guide.at, LH + GUIDE_OVERRUN);
            } else {
                g.moveTo(-GUIDE_OVERRUN, guide.at);
                g.lineTo(LW + GUIDE_OVERRUN, guide.at);
            }
        }
    }

    draw() {
        const {width: LW, height: LH} = this.leafSize();
        const lx = this.lite.offsetLeft.get();
        const ty = this.lite.offsetTop.get();
        const w = this.lite.width.get();
        const h = this.lite.height.get();
        const rx = lx + w;
        const by = ty + h;

        const selected = this.selectedFlag;
        // a lite over a stile line draws in the warning colour, selected or not.
        const warning = this.elev.doorSpec.stileViolations(this.lite).length > 0;
        const glass = this.glass;
        glass.clear();
        glass.lineStyle(
            selected ? SELECTED_STROKE : UNSELECTED_STROKE,
            warning ? lineRed : selected ? SELECTED_COLOR : lineBlueHeavy,
            1,
        );
        if (selected) {
            glass.beginFill(SELECTED_COLOR, SELECTED_ALPHA);
        } else if (warning) {
            glass.beginFill(lineRed, WARNING_ALPHA);
        } else {
            // unselected lites use the same diagonal glass gradient as the frame
            // grid lites so the two views read consistently. Map the (size x size)
            // texture onto this rect: scale to its dimensions, translate to its
            // top-left so it fills rather than tiles.
            const texture = getGlassTexture();
            const matrix = new Matrix();
            matrix.scale(w / texture.width, h / texture.height);
            matrix.translate(lx, ty);
            glass.beginTextureFill({texture, matrix});
        }
        glass.drawRect(lx, ty, w, h);
        glass.endFill();
        // widen the hit area slightly so thin lites stay easy to tap.
        glass.hitArea = new Rectangle(lx, ty, w, h);

        // resize handles ride the edges/corners; only shown while selected.
        this.updateHandles(selected, lx, ty, rx, by);

        this.markLabel.text = this.lite.mark;
        this.markLabel.tint = warning ? lineRed : lineBlueHeavy;
        this.markLabel.position.set(lx + w / 2, ty + h / 2);

        const dims = this.dims;
        dims.clear();

        // dimensions only show for the selected lite, or when "Show All Dimensions" is
        // on — otherwise the leaf stays uncluttered.
        const showDims = selected || !!this.elev.viewSettings.dimShowAll.value;
        if (!showDims) {
            for (const label of this.labels.values()) label.visible = false;
            return;
        }

        // centre axes of the lite: offsets run along them, out to the leaf edge.
        const midX = lx + w / 2;
        const midY = ty + h / 2;

        const specs: DimSpec[] = [
            {field: "offsetLeft", vertical: false, x1: 0, y1: midY, x2: lx, y2: midY},
            {field: "offsetRight", vertical: false, x1: rx, y1: midY, x2: LW, y2: midY},
            {field: "offsetTop", vertical: true, x1: midX, y1: 0, x2: midX, y2: ty},
            {field: "offsetBottom", vertical: true, x1: midX, y1: by, x2: midX, y2: LH},
            // inner dims, inset from the lite's top/left edge.
            {field: "width", vertical: false, x1: lx, y1: ty + Math.min(INNER_INSET, h / 2), x2: rx, y2: ty + Math.min(INNER_INSET, h / 2)},
            {field: "height", vertical: true, x1: lx + Math.min(INNER_INSET, w / 2), y1: ty, x2: lx + Math.min(INNER_INSET, w / 2), y2: by},
        ];

        for (const s of specs) {
            this.drawDim(dims, s);
        }
    }

    // position + paint the eight resize handles on the current rectangle; hide
    // them all when the lite isn't selected.
    private updateHandles(selected: boolean, lx: number, ty: number, rx: number, by: number) {
        for (const {g, desc} of this.handles) {
            if (!selected) {
                g.visible = false;
                continue;
            }

            const x = desc.h === "left" ? lx : desc.h === "right" ? rx : (lx + rx) / 2;
            const y = desc.v === "top" ? ty : desc.v === "bottom" ? by : (ty + by) / 2;

            g.visible = true;
            g.position.set(x, y);
            g.clear();
            g.lineStyle(1, lineBlueHeavy, 1);
            g.beginFill(SELECTED_COLOR, 1);
            g.drawRect(-HANDLE_HALF, -HANDLE_HALF, HANDLE_HALF * 2, HANDLE_HALF * 2);
            g.endFill();

            const hit = HANDLE_HALF + HANDLE_HIT_PAD;
            g.hitArea = new Rectangle(-hit, -hit, hit * 2, hit * 2);
        }
    }

    private drawDim(g: Graphics, s: DimSpec) {
        const frmVar = this.lite.varFor(s.field);
        const value = frmVar.get();

        const style = this.app.dimensionStyle;
        const color = frmVar.isModified() ? style.userColor : style.defaultLineColor;

        this.app.setDimensionLineStyle(g, color);
        g.moveTo(s.x1, s.y1);
        g.lineTo(s.x2, s.y2);

        // perpendicular end ticks.
        if (s.vertical) {
            g.moveTo(s.x1 - TICK, s.y1); g.lineTo(s.x1 + TICK, s.y1);
            g.moveTo(s.x2 - TICK, s.y2); g.lineTo(s.x2 + TICK, s.y2);
        } else {
            g.moveTo(s.x1, s.y1 - TICK); g.lineTo(s.x1, s.y1 + TICK);
            g.moveTo(s.x2, s.y2 - TICK); g.lineTo(s.x2, s.y2 + TICK);
        }

        const label = this.ensureLabel(s.field);
        label.visible = true;
        label.text = this.app.formatDimension(value);
        label.tint = color;

        const cx = (s.x1 + s.x2) / 2;
        const cy = (s.y1 + s.y2) / 2;
        if (s.vertical) {
            label.rotation = -Math.PI / 2;
            label.position.set(cx - LABEL_OFFSET, cy);
        } else {
            label.rotation = 0;
            label.position.set(cx, cy - LABEL_OFFSET);
        }
        label.hitArea = new Rectangle(-label.width * 0.5, -label.height * 0.5, label.width, label.height);
    }

    private ensureLabel(field: MdDoorLiteField): BitmapText {
        let label = this.labels.get(field);
        if (label) return label;

        label = new BitmapText("", TEXT_STYLE);
        label.anchor.set(0.5, 0.5);
        label.tint = lineBlueHeavy;
        label.eventMode = "static";
        label.cursor = "pointer";
        label.on("pointertap", (e) => {
            e.stopPropagation();
            this.openEditor(field, label!);
        });
        this.dims.addChild(label);
        this.labels.set(field, label);
        return label;
    }

    private openEditor(field: MdDoorLiteField, anchorEl: BitmapText) {
        if (this.elev.readOnly) return;
        const frmVar = this.lite.varFor(field);
        this.app.showReact({
            render: (opts) => this.app.renderDimension({
                value: frmVar.get(),
                locked: frmVar.isModified(),
                onLockChange: () => {
                    // unlock: let this dimension flex again on future edits.
                    frmVar.userClearModified();
                    opts.remove();
                    this.onEdit();
                },
                onDone: (value: number) => {
                    opts.remove();
                    const leaf = this.leafSize();
                    this.lite.editDimension(field, value, leaf.width, leaf.height);
                    this.onEdit();
                },
                onCancel: () => opts.remove(),
            }),
            anchor: () => anchorEl.getGlobalPosition(),
        });
    }
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
